import type { GridColDef } from '@mui/x-data-grid-pro';
import type { Participant } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import { createEditColumn, createIsActiveColumn, createSportIconColumn } from '../../helpers/columns';
import { defaultColumnsParticipants } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn } from '../../utils';
import TableTemplate from '../templates/TableTemplate';

const ParticipantsPage = () => {
  const {
    data: participantsData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<Participant>('participants');
  const { deleteData, updateData } = useMutateData('participants', [QUERY_KEYS.participants]);
  const { handleSort } = useSort(changeQuery);

  const columns: GridColDef[] = [
    createColumn('id', 'ID', {
      sortable: false,
    }),
    createColumn('name', 'Participant Name', { minWidth: 150 }),
    createColumn('shortName', 'Participant Short Name'),
    createColumn('sportName', 'Sport', { valueGetter: (value, row, column, apiRef) => row.sport.name }),
    createSportIconColumn<Participant>(),
    createColumn('competitionName', 'Competitions', {
      minWidth: 150,
      valueGetter: (value, row, column, apiRef) =>
        row.competitions.map((competition: Participant['competitions'][number]) => competition.name).join(', '),
    }),
    createColumn('tournamentName', 'Tournaments', {
      minWidth: 200,
      valueGetter: (value, row, column, apiRef) =>
        row.tournaments.map((tournament: Participant['tournaments'][number]) => tournament.name).join(', '),
    }),
    createIsActiveColumn<Participant>({
      updateData,
    }),
    createEditColumn<Participant>({
      deleteData,
      columnName: 'Actions',
    }),
  ];

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  return (
    <TableTemplate
      rows={participantsData?.items || []}
      columns={columns}
      rowCount={participantsData?.count || 0}
      loading={isFetching || isLoading}
      defaultVisibleColumns={defaultColumnsParticipants}
      createItemBtnLabel='Add new participant'
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      changeQuery={changeQuery}
      formType='participants'
      showExport
    />
  );
};

export default ParticipantsPage;
